import React, { useState, useMemo, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";

import { paletteGroups, needsCall, shortVerbTitle } from "./verbs";
import NodeIcon from "./NodeIcon";

const useStyles = makeStyles(theme => ({
  paper: { height: "78vh", maxHeight: 640 },
  header: { padding: theme.spacing(1.5, 2, 1), borderBottom: `1px solid ${theme.app.border}` },
  title: { fontSize: 15, fontWeight: 700, color: theme.app.dark, marginBottom: theme.spacing(1) },
  body: { display: "flex", padding: 0, height: "100%", overflow: "hidden" },
  // left rail: groups
  rail: {
    width: 220,
    flexShrink: 0,
    borderRight: `1px solid ${theme.app.border}`,
    overflowY: "auto",
    background: "#FAFAF7",
    padding: theme.spacing(1, 0.5),
  },
  groupRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    margin: "1px 4px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 13,
    color: theme.app.dark,
    "&:hover": { background: theme.app.selectedRow },
  },
  groupActive: { background: "#fff", boxShadow: "inset 0 0 0 1px " + theme.app.border, fontWeight: 700 },
  groupLabel: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  groupCount: { fontSize: 11, color: theme.app.textTertiary },
  // right pane: components
  pane: { flex: 1, overflowY: "auto", padding: theme.spacing(1.5, 2) },
  paneHint: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.app.textTertiary,
    margin: theme.spacing(0, 0, 1),
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "9px 10px",
    borderRadius: 10,
    border: `1px solid ${theme.app.border}`,
    background: "#fff",
    cursor: "pointer",
    marginBottom: 6,
    "&:hover": { borderColor: theme.app.lime, background: theme.app.selectedRow },
  },
  itemText: { minWidth: 0, flex: 1 },
  itemTitle: { fontSize: 13, fontWeight: 600, color: theme.app.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  itemSub: { fontSize: 11.5, color: theme.app.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  badge: {
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#8a6d00",
    background: "#FFF4CC",
    border: "1px solid #F2E3A0",
    borderRadius: 6,
    padding: "2px 6px",
  },
  empty: { color: theme.app.textTertiary, padding: theme.spacing(3, 0), textAlign: "center" },
}));

// minúsculas e sem acento ("Secretária" == "secretaria")
const norm = s =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
const words = s => norm(s).split(/[^a-z0-9]+/).filter(Boolean);

// Busca por PALAVRAS: cada termo digitado tem que bater com o começo de alguma
// palavra (ou estar dentro dela, a partir de 3 letras) — em qualquer ordem.
// "Enviar Wh" acha "Enviar mensagem WhatsApp"; "zap enviar" não.
// Pontua p/ ordenar: título > palavras-chave/grupo; começo do título vale mais.
const scoreVerb = (terms, v, g, title) => {
  const fields = [
    { ws: words(title), w: 3 },
    { ws: words(v.title).concat(words(v.label)), w: 2 },
    { ws: words(v.keywords).concat(words(g.label)), w: 1 },
  ];
  let score = 0;
  for (const term of terms) {
    let best = 0;
    for (const f of fields) {
      for (const w of f.ws) {
        const s = w === term ? f.w + 1 : w.startsWith(term) ? f.w + 0.5 : term.length >= 3 && w.includes(term) ? f.w * 0.5 : 0;
        if (s > best) best = s;
      }
    }
    if (!best) return 0; // todo termo precisa bater em algum lugar
    score += best;
  }
  if (norm(title).startsWith(terms.join(" "))) score += 2;
  return score;
};

const AddNodeModal = ({
  open,
  onClose,
  onPick,
  title = "Adicionar componente",
  hasCall = true,
  toolMode = false,
  collectorMode = false,
}) => {
  const classes = useStyles();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const groups = useMemo(() => paletteGroups({ tool: toolMode, collector: collectorMode }), [toolMode, collectorMode]);
  const query = norm(q.trim());
  const terms = useMemo(() => words(q), [q]);

  // default selection = first group; reset query when (re)opened
  useEffect(() => {
    if (open) {
      setQ("");
      setActive(g => g || (groups[0] && groups[0].key));
    }
  }, [open, groups]);

  // search across every group -> flat list with the originating group
  const matches = useMemo(() => {
    if (!query || !terms.length) return null;
    const out = [];
    groups.forEach(g =>
      g.verbs.forEach(v => {
        const title = g.kind === "connector" ? shortVerbTitle(v.title, g.label) : v.title;
        const score = scoreVerb(terms, v, g, title);
        if (score) out.push({ v, g, score });
      })
    );
    // estável: empate mantém a ordem da paleta
    return out.map((m, i) => ({ ...m, i })).sort((a, b) => b.score - a.score || a.i - b.i);
  }, [query, terms, groups]);

  const activeGroup = groups.find(g => g.key === active) || groups[0];

  const renderItem = (v, g, withGroupSub) => {
    const isTrig = !!v.isTrigger;
    const blocked = !isTrig && !hasCall && needsCall(v.key);
    const clickable = !isTrig && !blocked;
    const title = g.kind === "connector" ? shortVerbTitle(v.title, g.label) : v.title;
    const sub = withGroupSub ? g.label : "";
    const node = (
      <div
        key={v.key}
        className={classes.item}
        style={
          blocked
            ? { opacity: 0.45, cursor: "not-allowed" }
            : isTrig
            ? { cursor: "default", background: "#FBFAF6" }
            : undefined
        }
        onClick={() => clickable && onPick(v.key)}
      >
        <NodeIcon verbKey={v.key} category={v.category} color={g.color} size={32} brand={g.connectorType} />
        <div className={classes.itemText}>
          <div className={classes.itemTitle}>{title}</div>
          {sub ? <div className={classes.itemSub}>{sub}</div> : null}
        </div>
        {isTrig && <span className={classes.badge}>Gatilho</span>}
      </div>
    );
    if (blocked) {
      return (
        <Tooltip key={v.key} title="Requer uma chamada em curso (use um gatilho de chamada ou o nó 'Iniciar chamada' antes)" placement="top">
          {node}
        </Tooltip>
      );
    }
    if (isTrig) {
      return (
        <Tooltip key={v.key} title="É um gatilho (inicia o fluxo) — configure no início, no seletor de gatilho." placement="top">
          {node}
        </Tooltip>
      );
    }
    return node;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        <div className={classes.title}>{title}</div>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          autoFocus
          placeholder="Buscar componente ou conector…"
          value={q}
          onChange={e => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <DialogContent className={classes.body}>
        {/* left rail: groups (hidden while searching) */}
        {!query && (
          <div className={classes.rail}>
            {groups.map(g => (
              <div
                key={g.key}
                className={`${classes.groupRow} ${g.key === active ? classes.groupActive : ""}`}
                onClick={() => setActive(g.key)}
              >
                {g.kind === "connector" ? (
                  <NodeIcon verbKey={g.verbs[0] && g.verbs[0].key} color={g.color} size={22} />
                ) : (
                  <span style={{ width: 22, height: 22, borderRadius: 6, background: g.color, flexShrink: 0 }} />
                )}
                <span className={classes.groupLabel}>{g.label}</span>
                <span className={classes.groupCount}>{g.verbs.length}</span>
              </div>
            ))}
          </div>
        )}

        {/* right pane: components */}
        <div className={classes.pane}>
          {query ? (
            matches.length === 0 ? (
              <Typography className={classes.empty}>Nenhum componente encontrado.</Typography>
            ) : (
              <>
                <div className={classes.paneHint}>{matches.length} resultado(s)</div>
                {matches.map(({ v, g }) => renderItem(v, g, true))}
              </>
            )
          ) : activeGroup ? (
            <>
              <div className={classes.paneHint}>{activeGroup.label}</div>
              {activeGroup.verbs.map(v => renderItem(v, activeGroup, false))}
            </>
          ) : null}
        </div>
      </DialogContent>

      <div style={{ padding: "8px 16px", borderTop: "1px solid #eee", textAlign: "right" }}>
        <Button onClick={onClose}>Fechar</Button>
      </div>
    </Dialog>
  );
};

export default AddNodeModal;
